import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Download, Pencil, Trash2, Upload, FileText, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { fetchEntityDocuments, documentDownloadUrl } from "../../lib/dataFetchers";
import type { Document } from "../../lib/types";
import { isInlineSafe } from "../../lib/types";
import { cn } from "../../lib/cn";
import { formatBytes } from "../../lib/formatBytes";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { UploadDocumentDialog } from "./UploadDocumentDialog";
import { DeleteDocumentDialog } from "./DeleteDocumentDialog";
import { EditDocumentDialog } from "./EditDocumentDialog";

interface DocumentsSectionProps {
  entityId: string;
  /** Passed canEdit(role) from EntityDetail — Upload/Delete visible only when true. */
  canManage: boolean;
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface DocumentRowProps {
  doc: Document;
  entityId: string;
  canManage: boolean;
  onEditClick: (doc: Document) => void;
  onDeleteClick: (doc: Document) => void;
}

function DocumentRow({ doc, entityId, canManage, onEditClick, onDeleteClick }: DocumentRowProps): JSX.Element {
  const { t } = useTranslation("documents");
  const inlineUrl = isInlineSafe(doc.contentType)
    ? documentDownloadUrl(entityId, doc.id, "inline")
    : null;
  const attachmentUrl = documentDownloadUrl(entityId, doc.id, "attachment");

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4",
        "py-3 border-b border-border-2 last:border-b-0",
      )}
    >
      {/* Document icon + name / meta */}
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <FileText
          className="h-[15px] w-[15px] text-dim mt-0.5 shrink-0"
          aria-hidden
        />
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-text truncate">{doc.name}</p>
          {doc.notes && (
            <p className="text-[12px] text-muted truncate mt-0.5">{doc.notes}</p>
          )}
          <p className="text-[11.5px] text-dim mt-0.5">
            <span className="truncate">{doc.originalFilename}</span>
            <span className="mx-1">·</span>
            <span>{formatBytes(doc.sizeBytes, t)}</span>
          </p>
          <p className="text-[11.5px] text-dim mt-0.5">
            {doc.uploadedBy && (
              <>
                <span>{doc.uploadedBy}</span>
                <span className="mx-1">·</span>
              </>
            )}
            <time dateTime={doc.createdAt}>{formatDateTime(doc.createdAt)}</time>
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 shrink-0 sm:justify-end">
        {inlineUrl && (
          <a
            href={inlineUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${t("documents:view_button")}: ${doc.name}`}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[7px] text-[12px] font-semibold",
              "border border-border text-text bg-surface hover:bg-surface-2",
              "min-h-[44px] sm:min-h-0 sm:h-8",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20",
            )}
          >
            <Eye className="h-3.5 w-3.5 text-dim" aria-hidden />
            {t("documents:view_button")}
          </a>
        )}

        <a
          href={attachmentUrl}
          download
          aria-label={`${t("documents:download_button")}: ${doc.name}`}
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[7px] text-[12px] font-semibold",
            "border border-border text-text bg-surface hover:bg-surface-2",
            "min-h-[44px] sm:min-h-0 sm:h-8",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20",
          )}
        >
          <Download className="h-3.5 w-3.5 text-dim" aria-hidden />
          {t("documents:download_button")}
        </a>

        {canManage && (
          <button
            type="button"
            onClick={() => onEditClick(doc)}
            aria-label={t("documents:edit_document_label", { name: doc.name })}
            className={cn(
              "inline-flex items-center justify-center h-8 w-8 rounded-[7px]",
              "text-dim hover:text-accent hover:bg-accent-soft",
              "min-h-[44px] sm:min-h-0 sm:h-8 sm:w-8",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20",
            )}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}

        {canManage && (
          <button
            type="button"
            onClick={() => onDeleteClick(doc)}
            aria-label={`${t("documents:delete_button")}: ${doc.name}`}
            className={cn(
              "inline-flex items-center justify-center h-8 w-8 rounded-[7px]",
              "text-dim hover:text-red hover:bg-red-soft",
              "min-h-[44px] sm:min-h-0 sm:h-8 sm:w-8",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-red/20",
            )}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

export function DocumentsSection({ entityId, canManage }: DocumentsSectionProps): JSX.Element {
  const { t } = useTranslation(["documents", "common"]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [editTarget, setEditTarget] = useState<Document | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["entity-documents", entityId],
    queryFn: () => fetchEntityDocuments(entityId),
  });

  const documents = data ?? [];

  const uploadAction = canManage ? (
    <Button variant="xs-secondary" onClick={() => setUploadOpen(true)}>
      <Upload className="h-[13px] w-[13px]" aria-hidden />
      {t("documents:upload_button")}
    </Button>
  ) : undefined;

  return (
    <SectionCard
      title={t("documents:documents_card_title")}
      action={uploadAction}
    >
      {isLoading ? (
        <div className="space-y-3 py-1" aria-label={t("documents:loading")}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-14 rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <AlertCircle className="h-8 w-8 text-red/60" aria-hidden />
          <p className="text-[13px] text-muted">{t("documents:error")}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-[12.5px] text-accent hover:underline min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded"
          >
            {t("documents:retry")}
          </button>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-dim text-[13px] py-[18px] text-center border border-dashed border-border rounded-[9px] my-1">
          <FileText className="h-8 w-8 text-dim/40 mx-auto mb-2" aria-hidden />
          <p className="font-medium text-muted text-[13px]">{t("documents:empty_state_title")}</p>
          <p className="text-[12px] text-dim mt-1 max-w-xs mx-auto">
            {canManage
              ? t("documents:empty_state_desc_manage")
              : t("documents:empty_state_desc_view")}
          </p>
        </div>
      ) : (
        <div>
          {documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              entityId={entityId}
              canManage={canManage}
              onEditClick={(d) => setEditTarget(d)}
              onDeleteClick={(d) => setDeleteTarget(d)}
            />
          ))}
        </div>
      )}

      {canManage && (
        <>
          <UploadDocumentDialog
            entityId={entityId}
            open={uploadOpen}
            onOpenChange={setUploadOpen}
          />

          {deleteTarget && (
            <DeleteDocumentDialog
              document={deleteTarget}
              entityId={entityId}
              open={deleteTarget !== null}
              onOpenChange={(v) => {
                if (!v) setDeleteTarget(null);
              }}
            />
          )}

          {editTarget && (
            <EditDocumentDialog
              document={editTarget}
              entityId={entityId}
              open={editTarget !== null}
              onOpenChange={(v) => {
                if (!v) setEditTarget(null);
              }}
            />
          )}
        </>
      )}
    </SectionCard>
  );
}
